import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { suppliersAPI, scopesAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { 
  Plus, Search, ArrowLeft, Building2, MoreHorizontal,
  Filter, X, Eye, Edit, Play, ClipboardCheck, RefreshCw, Trash2, FileSpreadsheet,
  ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, Download
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  'Created': { label: 'Created', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  'Approved': { label: 'Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'On Hold': { label: 'On Hold', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  'No Longer Needed': { label: 'No Longer Needed', className: 'bg-red-50 text-red-700 border-red-200' },
  'Under re-evaluation': { label: 'Under Re-evaluation', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  'Overdue': { label: 'Overdue', className: 'bg-red-100 text-red-700 border-red-300' },
  'Pending Approval': { label: 'Pending Approval', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  'Rejected': { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-300' },
};

export default function SuppliersPage() {
  const { user, isAdmin, canMakeEntries } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [textSearch, setTextSearch] = useState('');
  const initialStatus = searchParams.get('status') || '';
  const initialOverdue = searchParams.get('overdue') === 'true';
  const [selectedStatuses, setSelectedStatuses] = useState(
    initialOverdue ? ['Overdue'] : (initialStatus ? initialStatus.split(',') : [])
  );
  const [scopeFilter, setScopeFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(!!initialStatus || initialOverdue);
  const [activeTab, setActiveTab] = useState('as'); // 'as' or 'non_as'
  
  // Sorting state
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const isPurchaseManager = isAdmin || user?.app_roles?.supplier_mgmt === 'manager';
  const isQualityManager = isAdmin || user?.role === 'quality_manager';
  // Both manager and app_user can create/edit suppliers
  const canCreate = canMakeEntries('supplier_mgmt');

  const toggleStatus = (val) => {
    setSelectedStatuses(prev => 
      prev.includes(val) ? prev.filter(s => s !== val) : [...prev, val]
    );
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction or clear
      if (sortDir === 'asc') {
        setSortBy('');
        setSortDir('desc');
      } else {
        setSortDir('asc');
      }
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-300" />;
    return sortDir === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-emerald-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-emerald-600" />;
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      searchSuppliers();
    }
  }, [search, textSearch, selectedStatuses, scopeFilter, cityFilter, typeFilter, sortBy, sortDir]);

  const loadData = async () => {
    try {
      const params = {};
      const initialStatus = searchParams.get('status');
      const initialOverdue = searchParams.get('overdue');
      if (initialStatus) params.status = initialStatus;
      if (initialOverdue === 'true') params.overdue = true;
      
      const [suppliersRes, scopesRes] = await Promise.all([
        suppliersAPI.list(params),
        scopesAPI.list()
      ]);
      setSuppliers(suppliersRes.data);
      setScopes(scopesRes.data);
      
      // Extract unique cities
      const uniqueCities = [...new Set(suppliersRes.data.map(s => s.city).filter(Boolean))].sort();
      setCities(uniqueCities);
    } catch (err) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const searchSuppliers = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (textSearch) params.text_search = textSearch;
      if (selectedStatuses.length > 0) params.status = selectedStatuses.join(',');
      if (scopeFilter && scopeFilter !== 'all') params.scope = scopeFilter;
      if (cityFilter && cityFilter !== 'all') params.city = cityFilter;
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
      }
      if (typeFilter && typeFilter !== 'all') params.supplier_type = typeFilter;
      
      const res = await suppliersAPI.list(params);
      setSuppliers(res.data);
      
      // Update cities list if not filtering
      if (!cityFilter || cityFilter === 'all') {
        const uniqueCities = [...new Set(res.data.map(s => s.city).filter(Boolean))].sort();
        setCities(uniqueCities);
      }
    } catch (err) {
      console.error('Search failed', err);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setTextSearch('');
    setSelectedStatuses([]);
    setScopeFilter('all');
    setCityFilter('all');
    setTypeFilter('all');
    setSortBy('');
    setSortDir('desc');
  };

  const handleStartApproval = async (e, supplier) => {
    e.stopPropagation();
    const label = supplier.approval_status === 'Created' ? 'approval' : 'reevaluation';
    if (!window.confirm(`Start ${label} process for "${supplier.supplier_name}"?`)) return;
    try {
      await suppliersAPI.startReevaluation(supplier.id);
      toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} process started`);
      searchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to start ${label}`);
    }
  };

  const hasActiveFilters = search || textSearch || selectedStatuses.length > 0 || 
    (scopeFilter && scopeFilter !== 'all') || (cityFilter && cityFilter !== 'all') || (typeFilter && typeFilter !== 'all') || sortBy;

  const getScopeName = (scopeId) => {
    const scope = scopes.find(s => s.id === scopeId);
    return scope?.name || scopeId;
  };

  const getActions = (supplier) => {
    const actions = [];
    const status = supplier.approval_status;
    const reevalStatus = supplier.reevaluation_status;

    // View is always available
    actions.push({ type: 'view', label: 'View Details', icon: Eye });

    // Edit - purchase manager or admin
    if (isPurchaseManager) {
      actions.push({ type: 'edit', label: 'Edit Supplier', icon: Edit });
    }

    // Status-based actions
    if (status === 'Created' && isPurchaseManager) {
      actions.push({ type: 'separator' });
      actions.push({ type: 'start_approval', label: 'Start Approval', icon: Play });
    } else if (status === 'Approved' && isPurchaseManager) {
      actions.push({ type: 'separator' });
      actions.push({ type: 'start_reeval', label: 'Start Reevaluation', icon: RefreshCw });
    } else if (status === 'On Hold' && isPurchaseManager) {
      actions.push({ type: 'separator' });
      actions.push({ type: 'start_reeval', label: 'Start Reevaluation', icon: RefreshCw });
    } else if (status === 'Under re-evaluation') {
      if (reevalStatus === 'pending_purchase' && isPurchaseManager) {
        actions.push({ type: 'separator' });
        actions.push({ type: 'submit_review', label: 'Submit Purchase Review', icon: ClipboardCheck });
      } else if (reevalStatus === 'pending_quality' && isQualityManager) {
        actions.push({ type: 'separator' });
        actions.push({ type: 'submit_review', label: 'Submit Quality Review', icon: ClipboardCheck });
      }
    }

    // Delete - admin only, not when under reevaluation
    if (isAdmin && status !== 'Under re-evaluation') {
      actions.push({ type: 'separator' });
      actions.push({ type: 'delete', label: 'Delete Supplier', icon: Trash2, destructive: true });
    }

    // NON-AS pending approval — admin can approve/reject
    if (supplier.supplier_type === 'NON-AS' && status === 'Pending Approval' && isAdmin) {
      actions.push({ type: 'separator' });
      actions.push({ type: 'approve_non_as', label: 'Approve', icon: ClipboardCheck });
      actions.push({ type: 'reject_non_as', label: 'Reject', icon: X, destructive: true });
    }

    return actions;
  };

  const handleDeleteSupplier = async (e, supplier) => {
    e.stopPropagation();
    if (!window.confirm(`Delete supplier "${supplier.supplier_name}"? This cannot be undone.`)) return;
    try {
      await suppliersAPI.delete(supplier.id);
      toast.success('Supplier deleted');
      searchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete supplier');
    }
  };

  const handleApproveNonAs = async (e, supplier) => {
    e.stopPropagation();
    if (!window.confirm(`Approve NON-AS supplier "${supplier.supplier_name}"?`)) return;
    try {
      await suppliersAPI.approveNonAs(supplier.id);
      toast.success(`${supplier.supplier_name} approved`);
      searchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve supplier');
    }
  };

  const handleRejectNonAs = async (e, supplier) => {
    e.stopPropagation();
    if (!window.confirm(`Reject NON-AS supplier "${supplier.supplier_name}"?`)) return;
    try {
      await suppliersAPI.rejectNonAs(supplier.id);
      toast.success(`${supplier.supplier_name} rejected`);
      searchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject supplier');
    }
  };

  const handleAction = (e, action, supplier) => {
    e.stopPropagation();
    switch (action.type) {
      case 'view':
        navigate(`/suppliers/${supplier.id}`);
        break;
      case 'edit':
        navigate(`/suppliers/${supplier.id}/edit`);
        break;
      case 'start_approval':
      case 'start_reeval':
        handleStartApproval(e, supplier);
        break;
      case 'submit_review':
        navigate(`/suppliers/${supplier.id}/reevaluate`);
        break;
      case 'delete':
        handleDeleteSupplier(e, supplier);
        break;
      case 'approve_non_as':
        handleApproveNonAs(e, supplier);
        break;
      case 'reject_non_as':
        handleRejectNonAs(e, supplier);
        break;
      default:
        break;
    }
  };

  const [exportingAsl, setExportingAsl] = useState(false);
  const handleExportAsl = async () => {
    setExportingAsl(true);
    try {
      const res = await suppliersAPI.exportAslReport();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `Approved_Supplier_List_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Approved Supplier List exported');
    } catch (err) {
      toast.error('Failed to export ASL report');
    } finally {
      setExportingAsl(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/suppliers')}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
              <div className="h-4 w-px bg-slate-300" />
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                <h1 className="text-lg font-semibold text-[#0F172A]">Supplier Management</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={handleExportAsl}
                disabled={exportingAsl}
                data-testid="export-asl-report-button"
              >
                <Download className="w-4 h-4" /> {exportingAsl ? 'Exporting...' : 'Export ASL'}
              </Button>
              {canCreate && (
                <>
                  {isAdmin && (
                    <Link to="/suppliers/import">
                      <Button size="sm" variant="outline" className="gap-1" data-testid="import-excel-button">
                        <FileSpreadsheet className="w-4 h-4" /> Import Excel
                      </Button>
                    </Link>
                  )}
                  <Link to="/suppliers/new">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1">
                      <Plus className="w-4 h-4" /> Add Supplier
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Switcher: AS vs NON-AS */}
        <div className="flex items-center gap-1 mb-4 p-1 bg-slate-100 rounded-lg w-fit" data-testid="supplier-tabs">
          <button
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'as' ? 'bg-white shadow text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`}
            onClick={() => setActiveTab('as')}
            data-testid="tab-as-suppliers"
          >
            AS Suppliers ({suppliers.filter(s => s.supplier_type !== 'NON-AS').length})
          </button>
          <button
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeTab === 'non_as' ? 'bg-white shadow text-amber-700' : 'text-slate-500 hover:text-slate-700'}`}
            onClick={() => setActiveTab('non_as')}
            data-testid="tab-nonas-suppliers"
          >
            NON-AS Suppliers ({suppliers.filter(s => s.supplier_type === 'NON-AS').length})
            {suppliers.filter(s => s.supplier_type === 'NON-AS' && s.approval_status === 'Pending Approval').length > 0 && (
              <Badge className="bg-orange-500 text-white text-[9px] px-1.5 py-0 ml-0.5">
                {suppliers.filter(s => s.supplier_type === 'NON-AS' && s.approval_status === 'Pending Approval').length}
              </Badge>
            )}
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search suppliers by name, code, city, or contact..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="supplier-search"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`gap-1 ${hasActiveFilters ? 'border-emerald-500 text-emerald-600' : ''}`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && <Badge className="ml-1 bg-emerald-500 text-white text-[10px]">Active</Badge>}
            </Button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Status</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-9 text-xs gap-1.5 w-48 justify-start" data-testid="supplier-status-filter-trigger">
                        <Filter className="w-3 h-3" />
                        {selectedStatuses.length === 0 ? 'All statuses' : selectedStatuses.length === 1 ? STATUS_LABELS[selectedStatuses[0]]?.label : `${selectedStatuses.length} selected`}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-52 p-2" align="start">
                      <div className="space-y-1">
                        {['Created', 'Approved', 'On Hold', 'Under re-evaluation', 'No Longer Needed', 'Overdue'].map(opt => (
                          <label key={opt} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                            <Checkbox
                              checked={selectedStatuses.includes(opt)}
                              onCheckedChange={() => toggleStatus(opt)}
                              className="h-3.5 w-3.5"
                            />
                            <span className={`text-xs ${opt === 'Overdue' ? 'text-red-600 font-medium' : ''}`}>
                              {STATUS_LABELS[opt]?.label || opt}
                            </span>
                            {opt === 'Overdue' && <AlertTriangle className="w-3 h-3 text-red-500" />}
                          </label>
                        ))}
                      </div>
                      {selectedStatuses.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setSelectedStatuses([])} className="w-full mt-2 text-xs text-slate-500">
                          <X className="w-3 h-3 mr-1" /> Clear
                        </Button>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="w-48">
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Scope of Approval</label>
                  <Select value={scopeFilter} onValueChange={setScopeFilter}>
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="All scopes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All scopes</SelectItem>
                      {scopes.map(scope => (
                        <SelectItem key={scope.id} value={scope.id}>{scope.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <label className="text-xs font-medium text-slate-500 mb-1 block">City</label>
                  <Select value={cityFilter} onValueChange={setCityFilter}>
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="All cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All cities</SelectItem>
                      {cities.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Supplier Type</label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="h-9 text-xs" data-testid="supplier-type-filter">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="AS">AS (Standard)</SelectItem>
                      <SelectItem value="NON-AS">NON-AS (General)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-xs text-slate-500"
                  >
                    <X className="w-3 h-3 mr-1" /> Clear all
                  </Button>
                )}
              </div>
              
              {/* Text search for notes */}
              <div className="flex gap-3 items-end">
                <div className="flex-1 max-w-md">
                  <label className="text-xs font-medium text-slate-500 mb-1 block">
                    Search in Notes (Scope & Criteria text)
                  </label>
                  <Input
                    value={textSearch}
                    onChange={(e) => setTextSearch(e.target.value)}
                    placeholder="Search scope/criteria notes..."
                    className="h-9 text-xs"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            {suppliers.filter(s => activeTab === 'non_as' ? s.supplier_type === 'NON-AS' : s.supplier_type !== 'NON-AS').length} supplier{suppliers.filter(s => activeTab === 'non_as' ? s.supplier_type === 'NON-AS' : s.supplier_type !== 'NON-AS').length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Suppliers Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold text-slate-500">Supplier ID</TableHead>
                <TableHead 
                  className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-emerald-600"
                  onClick={() => handleSort('name')}
                >
                  <span className="flex items-center">Name {getSortIcon('name')}</span>
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">SAP Code</TableHead>
                <TableHead 
                  className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-emerald-600"
                  onClick={() => handleSort('city')}
                >
                  <span className="flex items-center">City {getSortIcon('city')}</span>
                </TableHead>
                <TableHead 
                  className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-emerald-600"
                  onClick={() => handleSort('status')}
                >
                  <span className="flex items-center">Status {getSortIcon('status')}</span>
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Scope</TableHead>
                <TableHead 
                  className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-emerald-600"
                  onClick={() => handleSort('rating')}
                >
                  <span className="flex items-center">SR {getSortIcon('rating')}</span>
                </TableHead>
                <TableHead 
                  className="text-xs font-semibold text-slate-500 cursor-pointer hover:text-emerald-600"
                  onClick={() => handleSort('reevaluation_date')}
                >
                  <span className="flex items-center">Reeval Date {getSortIcon('reevaluation_date')}</span>
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 w-[60px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : suppliers.filter(s => activeTab === 'non_as' ? s.supplier_type === 'NON-AS' : s.supplier_type !== 'NON-AS').length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                    No {activeTab === 'non_as' ? 'NON-AS ' : ''}suppliers found
                  </TableCell>
                </TableRow>
              ) : (
                suppliers
                  .filter(s => activeTab === 'non_as' ? s.supplier_type === 'NON-AS' : s.supplier_type !== 'NON-AS')
                  .map((supplier) => {
                  const statusStyle = STATUS_LABELS[supplier.approval_status] || STATUS_LABELS['Created'];
                  const actions = getActions(supplier);
                  const isOverdue = supplier.is_overdue;
                  return (
                    <TableRow 
                      key={supplier.id} 
                      className={`hover:bg-slate-50/50 cursor-pointer ${isOverdue ? 'bg-red-50/30' : ''}`}
                      onClick={() => navigate(`/suppliers/${supplier.id}`)}
                      data-testid={`supplier-row-${supplier.id}`}
                    >
                      <TableCell className="text-xs font-mono text-slate-500">
                        {supplier.supplier_id}
                      </TableCell>
                      <TableCell className="font-medium text-[#0F172A]">
                        <div className="flex items-center gap-1.5">
                          {supplier.supplier_name}
                          {supplier.supplier_type === 'NON-AS' && (
                            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-300 shrink-0">NON-AS</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {supplier.supplier_code || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {supplier.city}, {supplier.country}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] ${statusStyle.className}`}>
                            {statusStyle.label}
                          </Badge>
                          {isOverdue && (
                            <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-300">
                              <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Overdue
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {supplier.scope_of_approval?.length > 0 
                          ? supplier.scope_of_approval.map(s => getScopeName(s)).slice(0, 2).join(', ')
                          : '-'}
                        {supplier.scope_of_approval?.length > 2 && (
                          <span className="text-slate-400"> +{supplier.scope_of_approval.length - 2}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs font-mono" data-testid={`sr-${supplier.id}`}>
                        {supplier.supplier_rating != null ? (
                          <span className={`font-semibold ${supplier.supplier_rating >= 70 ? 'text-emerald-600' : supplier.supplier_rating >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                            {supplier.supplier_rating}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className={`text-xs font-mono ${isOverdue ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                        {supplier.reevaluation_date || '-'}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={`action-menu-${supplier.id}`}
                            >
                              <MoreHorizontal className="w-4 h-4 text-slate-400" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {actions.map((action, idx) =>
                              action.type === 'separator' ? (
                                <DropdownMenuSeparator key={idx} />
                              ) : (
                                <DropdownMenuItem
                                  key={idx}
                                  onClick={(e) => handleAction(e, action, supplier)}
                                  className={`text-xs gap-2 cursor-pointer ${action.destructive ? 'text-red-600 focus:text-red-600' : ''}`}
                                  data-testid={`action-${action.type}-${supplier.id}`}
                                >
                                  <action.icon className="w-3.5 h-3.5" />
                                  {action.label}
                                </DropdownMenuItem>
                              )
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
